import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQS = [
  {
    q: "How many bags can I set out?",
    a: "Basic Valet covers up to 1 bag per pickup night and Standard Valet covers up to 3. Need more on a given night? Add On-Demand bags at $4 each — no plan change required. Every bag must be tied and under 25 lbs.",
  },
  {
    q: "What happens if I miss my pickup window?",
    a: "If your bags aren't out during your selected window (6–9 AM or 6–9 PM), that night's pickup is missed. Simply bring bags back inside — hallways must stay clear outside the windows — and set them out during the next window. Your subscription continues as normal.",
  },
  {
    q: "Can I switch between the morning and evening windows?",
    a: "Yes. Your preferred window is collected at checkout, and you can switch anytime by contacting us — the change applies from the next service night.",
  },
  {
    q: "What items are not accepted?",
    a: "We cannot take hazardous materials (paint, chemicals, batteries), furniture, or loose untied waste. Bags over the 25 lb limit must be split into multiple bags.",
  },
  {
    q: "Can I cancel my monthly subscription?",
    a: "Anytime, no contracts and no cancellation fees. Service continues through the end of your paid month.",
  },
];

export default function Faq() {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-28 sm:py-36 bg-ink/40">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }} className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-5">
            FAQ & assurance
          </motion.p>
          <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl leading-tight">
            Questions, answered. <span className="text-stroke">Risk, covered.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-12 rounded-3xl border border-fern/50 bg-forest/30 p-8 grain relative overflow-hidden"
            data-testid="insurance-shield"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-fern flex items-center justify-center shrink-0">
                <ShieldCheck className="w-6 h-6 text-parchment" strokeWidth={1.8} />
              </div>
              <div>
                <p className="font-display font-extrabold text-2xl text-parchment">$1M / $2M</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist">General Liability Policy</p>
              </div>
            </div>
            <p className="mt-6 text-sm text-mist leading-relaxed">
              Every collection is performed under a $1M per-occurrence / $2M aggregate General Liability
              insurance policy, with property management named as <span className="text-parchment font-medium">Additional Insured</span>.
              Your building, your neighbors, and your home are protected on every single sweep.
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="lg:col-span-7"
        >
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`}
                className="rounded-2xl border border-white/8 bg-white/[0.03] px-6 data-[state=open]:border-terracotta/40 transition-colors duration-300"
                data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display font-semibold text-base py-5 hover:no-underline hover:text-terracotta transition-colors duration-300">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-mist leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
